import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import { ca } from '@cian/analytics';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { IAppState, IFiltersState } from '../../../../common/state';
import { IFeatureToggleState } from '../../../../common/state/types/featureToggle';
import { getTrackingData } from '../../../../selectors/getTrackingData';
import { trackAdFoxMortgage } from '../../../../tracking/adFoxMortgage';
import { sendAdsEvent } from '../../../../tracking/sendAdsEvent';
import { ISimplifieldTGBBannerContext } from '../../../../types/adfox';
import { useAdfoxContext } from '../../../../utils/adfox/context';
import { shouldSendAdsEvent as checkShouldSendAdsEvent } from '../../../../utils/shouldSendAdsEvent';
import { NativeTGBAsideInfo } from '../../../components/adfox/templates/tgb_in_card/simplified';
import { isMortgageCampaign } from '../../../components/adfox/utils/mortgage';

export interface IResidentialNativeTGBBannerProps {
  context: ISimplifieldTGBBannerContext;
  locationId: number | undefined;
}

export function ResidentialNativeTGBBanner({ context, locationId }: IResidentialNativeTGBBannerProps) {
  const tracking = useSelector(getTrackingData);
  const { position } = useAdfoxContext();
  const filters = useSelector<IAppState, IFiltersState>(state => state.filters);
  const featureToggle = useSelector<IAppState, IFeatureToggleState>(state => state.featureToggle);

  const shouldSendAdsEvent = React.useMemo(() => {
    return checkShouldSendAdsEvent(filters.jsonQuery) || !featureToggle.isFilterByFlatsEnabled;
  }, [filters.jsonQuery, featureToggle.isFilterByFlatsEnabled]);

  const onTrack = React.useCallback(
    (isShow: boolean) => {
      const { bannerId, campaignId } = context;
      const { user, page } = tracking;
      const realtyUserId = user.userId;

      if (!isMortgageCampaign(campaignId)) {
        if (shouldSendAdsEvent && !!bannerId) {
          sendAdsEvent({
            realtyUserId,
            action: isShow ? 'show' : 'click',
            bannerId,
            bannerPlace: 'offersSerp',
          });

          if (!isShow) {
            ca('teaser', {
              sc: filters.jsonQuery,
              product: {
                id: Number(bannerId),
                name: 'Listing_click_native_TGB',
                position,
              },
            });
          }
        }

        return;
      }

      trackAdFoxMortgage(isShow ? 'show' : 'click', 'LinkIpoteka', bannerId, user, page);
    },
    [context, tracking, position, shouldSendAdsEvent, filters.jsonQuery],
  );

  const onView = React.useCallback(() => {
    const { bannerId, campaignId } = context;

    if (!isMortgageCampaign(campaignId) && shouldSendAdsEvent && !!bannerId) {
      ca('teaser', {
        sc: filters.jsonQuery,
        product: {
          id: Number(bannerId),
          name: 'Listing_show_native_TGB',
          position,
        },
      });

      return;
    }
  }, [context, position, shouldSendAdsEvent, filters.jsonQuery]);

  return (
    <ActionAfterViewed callback={onView} percentVisible={0.5}>
      <NativeTGBAsideInfo context={context} locationId={locationId} onTrack={onTrack} />
    </ActionAfterViewed>
  );
}
